@if (isServer) {
<div class="fixed inset-0 flex items-center justify-center bg-base-100 z-50">
  <span
    class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"
  ></span>
</div>
} @if (!isServer) {
<div>
  <div class="px-4 mt-24 min-h-screen">
    <div class="max-w-md w-full mx-auto">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-base-content">Login</h1>
        <p class="text-base-content/70 mt-2">
          Continue with your existing account
        </p>
      </div>
      <!-- Card -->
      <div class="daisy-card bg-base-100 shadow-xl">
        <div class="daisy-card-body">
          <!-- Error Alert -->
          @if (loginError) {
          <div class="daisy-alert daisy-alert-error mb-4">
            <i class="fa-solid fa-circle-exclamation"></i>
            <span>{{ loginError }}</span>
          </div>
          }
          <!-- Login Form -->
          <form [formGroup]="loginForm" class="space-y-4">
            <!-- Username Field -->
            <div class="daisy-form-control w-full">
              <label class="daisy-label">
                <span class="daisy-label-text">Username</span>
              </label>
              <input
                type="text"
                formControlName="username"
                placeholder="Email Address or Mobile Number"
                class="daisy-input daisy-input-bordered w-full"
                [ngClass]="{
                  'daisy-input-error':
                    loginForm.invalid && (username?.dirty || username?.touched)
                }"
              />
              @if ( loginForm.invalid && (username?.dirty || username?.touched)
              ) {
              <div class="daisy-label mt-1">
                @if (username?.hasError('required')) {
                <span class="daisy-label-text-alt text-error">
                  Username is required
                </span>
                } @if (username?.hasError('emailPhone')) {
                <span class="daisy-label-text-alt text-error">
                  Username should be valid email or mobile number
                </span>
                }
              </div>
              }
            </div>
            <!-- Password Field -->
            <div class="daisy-form-control w-full">
              <label class="daisy-label">
                <span class="daisy-label-text">Password</span>
              </label>
              <div class="relative">
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  formControlName="password"
                  placeholder="Password"
                  class="daisy-input daisy-input-bordered w-full pr-10"
                  [ngClass]="{
                    'daisy-input-error':
                      password?.invalid && (password?.dirty || password?.touched)
                  }"
                />
                <button
                  type="button"
                  (click)="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-base-content focus:outline-none"
                  [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
                >
                  <i [class]="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                </button>
              </div>
              @if ( password?.invalid && (password?.dirty || password?.touched)
              ) {
              <div class="daisy-label mt-1">
                @if (password?.hasError('required')) {
                <span class="daisy-label-text-alt text-error">
                  Password is required
                </span>
                }
              </div>
              }
            </div>
            <!-- Submit Button -->
            <button
              (click)="submit()"
              class="daisy-btn daisy-btn-primary w-full mt-6"
              [disabled]="loginForm.invalid"
            >
              Log in
            </button>
            <!-- Forgot Password Link -->
            <div class="text-center mt-4">
              <a
                routerLink="/auth/reset-password"
                class="text-primary hover:text-primary-focus transition-colors"
              >
                Forgot your password?
              </a>
            </div>
            <!-- Google Sign-In Button -->
            @if (isGoogleLoginEnabled) {
              <div class="daisy-divider">OR</div>
              <div class="relative w-full">
                @if (isGoogleLoading) {
                <button type="button" class="daisy-btn daisy-btn-outline daisy-btn-primary w-full gap-2 pointer-events-none">
                  <span class="daisy-loading daisy-loading-spinner daisy-loading-sm text-primary"></span>
                  <span>Authenticating with Google...</span>
                </button>
                } @else {
                <div
                  id="google-signin-button"
                  class="w-full flex justify-center"
                ></div>
                }
              </div>
            }
            <!-- Register Link -->
            <div class="daisy-divider">New User?</div>
            <div class="text-center">
              <p class="text-base-content/70">
                Are you new here?
                <a
                  routerLink="/auth/register"
                  class="text-primary hover:text-primary-focus transition-colors ml-1"
                >
                  Register
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Google Onboarding Modal -->
  @if (showGoogleOnboarding) {
  <div class="daisy-modal daisy-modal-open">
    <div class="daisy-modal-box">
      <h3 class="font-bold text-lg text-base-content">Complete Google Sign-Up</h3>
      <p class="py-2 text-sm text-base-content/70">
        Please enter your Date of Birth to complete your registration.
      </p>

      <form [formGroup]="googleOnboardingForm" (ngSubmit)="submitGoogleOnboarding()" class="space-y-4 mt-2">
        <div class="daisy-form-control w-full">
          <label class="daisy-label">
            <span class="daisy-label-text font-medium">Date of Birth</span>
          </label>
          <input
            type="date"
            formControlName="dateOfBirth"
            class="daisy-input daisy-input-bordered w-full"
            [ngClass]="{
              'daisy-input-error':
                isFieldInvalid(googleOnboardingForm, 'dateOfBirth') &&
                hasFieldError(googleOnboardingForm, 'dateOfBirth', 'required')
            }"
          />
          @if (isFieldInvalid(googleOnboardingForm, 'dateOfBirth') && hasFieldError(googleOnboardingForm, 'dateOfBirth', 'required')) {
          <div class="daisy-label mt-1">
            <span class="daisy-label-text-alt text-error">
              Date of Birth is required
            </span>
          </div>
          }
        </div>

        @if (isGoogleUnder16) {
        <div class="daisy-form-control w-full">
          <label class="daisy-label">
            <span class="daisy-label-text font-medium">Parent/Guardian Email</span>
          </label>
          <input
            type="email"
            formControlName="parentEmail"
            placeholder="Parent/Guardian Email Address"
            class="daisy-input daisy-input-bordered w-full"
            [ngClass]="{
              'daisy-input-error':
                isFieldInvalid(googleOnboardingForm, 'parentEmail') &&
                (hasFieldError(googleOnboardingForm, 'parentEmail', 'required') || hasFieldError(googleOnboardingForm, 'parentEmail', 'email'))
            }"
          />
          @if (isFieldInvalid(googleOnboardingForm, 'parentEmail') && (hasFieldError(googleOnboardingForm, 'parentEmail', 'required') || hasFieldError(googleOnboardingForm, 'parentEmail', 'email'))) {
          <div class="daisy-label mt-1">
            @if (hasFieldError(googleOnboardingForm, 'parentEmail', 'required')) {
            <span class="daisy-label-text-alt text-error">
              Parent/Guardian email is required for users under 16
            </span>
            } @if (hasFieldError(googleOnboardingForm, 'parentEmail', 'email')) {
            <span class="daisy-label-text-alt text-error">
              Please enter a valid email address
            </span>
            }
          </div>
          }
        </div>
        <div class="daisy-alert daisy-alert-warning text-xs mt-2">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>Since you are under 16, parental consent is required. An email will be sent to your parent/guardian to verify your account.</span>
        </div>
        }

        <div class="daisy-modal-action">
          <button
            type="button"
            (click)="showGoogleOnboarding = false"
            class="daisy-btn daisy-btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="daisy-btn daisy-btn-primary"
            [disabled]="googleOnboardingForm.invalid || isGoogleLoading"
          >
            @if (isGoogleLoading) {
            <span class="daisy-loading daisy-loading-spinner daisy-loading-sm"></span>
            } @else { Continue }
          </button>
        </div>
      </form>
    </div>
  </div>
  }
</div>
}
